import React, { useState, useEffect } from 'react';

const ProgramReports = () => {
  const [reports, setReports] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchReports();
    fetchPrograms();
  }, [filterStatus]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const url = filterStatus === 'all' 
        ? `${backendUrl}/api/crm/program-reports/reports`
        : `${backendUrl}/api/crm/program-reports/reports?status=${filterStatus}`;
      const response = await fetch(url);
      const data = await response.json();
      setReports(data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/crm/program-reports/programs`);
      const data = await response.json();
      setPrograms(data.programs || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const handleSendReminders = async () => {
    if (!window.confirm('Send reminder emails to all staff members?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/crm/program-reports/send-reminders`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        alert(`✅ Reminders sent to: ${data.recipients.join(', ')}`);
      } else {
        alert('Failed to send reminders');
      }
    } catch (error) {
      alert('Error sending reminders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReport = async (reportId) => {
    const approverName = window.prompt('Enter your name to approve this report:');
    if (!approverName) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/crm/program-reports/reports/${reportId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: approverName })
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ Report approved! You can now send it to the producer.');
        fetchReports();
      } else {
        alert('Failed to approve report: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      alert('Error approving report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReport = async (reportId) => {
    if (!window.confirm('Send this approved report to the producer?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/crm/program-reports/reports/${reportId}/send`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ Report sent successfully to the producer!');
        fetchReports();
      } else {
        alert('Failed to send report: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      alert('Error sending report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this draft report?')) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/crm/program-reports/reports/${reportId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        fetchReports();
      } else {
        alert('Failed to delete report');
      }
    } catch (error) {
      alert('Error deleting report: ' + error.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800'
    };
    const labels = {
      draft: 'DRAFT',
      approved: 'APPROVED ✓',
      sent: 'SENT'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status?.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📊 Program Reports</h2>
          <p className="text-gray-600 mt-1">Create and send performance reports to program producers</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSendReminders}
            disabled={loading}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
          >
            📧 Send Staff Reminders
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            ➕ Create New Report
          </button>
        </div>
      </div>

      {/* Programs Overview */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-6">
        <h3 className="font-semibold text-green-800 mb-3">📻 Programs Requiring Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {programs.map((program) => (
            <div key={program.id} className="bg-white rounded-lg p-3 shadow-sm">
              <p className="font-medium text-gray-900">{program.program_name}</p>
              <p className="text-sm text-gray-500">Producer: {program.producer_name}</p>
              <p className="text-xs text-gray-400">{program.producer_email} • {program.report_frequency}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="all">All Reports</option>
          <option value="draft">Drafts Only</option>
          <option value="sent">Sent Only</option>
        </select>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin text-4xl">⏳</div>
          <p className="mt-2 text-gray-500">Loading reports...</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-12 text-center">
          <span className="text-6xl">📋</span>
          <p className="mt-4 text-gray-600">No reports found</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Create Your First Report
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <div key={report.report_id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-lg text-gray-900">{report.program_name}</h3>
                    {getStatusBadge(report.status)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Report Period:</span>
                      <p className="font-medium">{report.report_month}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Producer:</span>
                      <p className="font-medium">{report.producer_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Created:</span>
                      <p className="font-medium">{formatDate(report.created_at)}</p>
                    </div>
                    {report.sent_at && (
                      <div>
                        <span className="text-gray-500">Sent:</span>
                        <p className="font-medium">{formatDate(report.sent_at)}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Preview */}
                  {report.listener_feedback && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <span className="text-xs text-gray-500">Listener Feedback Preview:</span>
                      <p className="text-sm text-gray-700 line-clamp-2">{report.listener_feedback}</p>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => setSelectedReport(report)}
                    className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                  >
                    👁️ View
                  </button>
                  {report.status === 'draft' && (
                    <>
                      <button
                        onClick={() => handleApproveReport(report.report_id)}
                        className="px-3 py-1 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                      >
                        ✅ Approve
                      </button>
                      <button
                        onClick={() => handleDeleteReport(report.report_id)}
                        className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                      >
                        🗑️
                      </button>
                    </>
                  )}
                  {report.status === 'approved' && (
                    <button
                      onClick={() => handleSendReport(report.report_id)}
                      className="px-3 py-1 text-sm bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100"
                    >
                      📤 Send
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Report Modal */}
      {showCreateModal && (
        <CreateReportModal
          programs={programs}
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            fetchReports();
            setShowCreateModal(false);
          }}
          backendUrl={backendUrl}
        />
      )}

      {/* View Report Modal */}
      {selectedReport && (
        <ViewReportModal
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          onApprove={() => {
            handleApproveReport(selectedReport.report_id);
            setSelectedReport(null);
          }}
          onSend={() => {
            handleSendReport(selectedReport.report_id);
            setSelectedReport(null);
          }}
        />
      )}
    </div>
  );
};

// Create Report Modal Component
const CreateReportModal = ({ programs, onClose, onSave, backendUrl }) => {
  const [formData, setFormData] = useState({
    program_id: '',
    program_name: '',
    producer_name: '',
    producer_email: '',
    report_month: new Date().toISOString().slice(0, 7),
    listener_feedback: '',
    impact_stories: '',
    broadcast_metrics: '',
    digital_metrics: '',
    challenges: '',
    recommendations: '',
    additional_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleProgramChange = (programId) => {
    const program = programs.find(p => p.id === programId);
    if (program) {
      setFormData({
        ...formData,
        program_id: program.id,
        program_name: program.program_name,
        producer_name: program.producer_name,
        producer_email: program.producer_email
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${backendUrl}/api/crm/program-reports/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        onSave();
      } else {
        setError(data.detail || 'Failed to create report');
      }
    } catch (err) {
      setError('Error creating report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">📝 Create Program Report</h2>
              <p className="text-gray-500 text-sm mt-1">Fill in the performance data for the producer</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Program Selection */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">📻 Program Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Program *</label>
                  <select
                    required
                    value={formData.program_id}
                    onChange={(e) => handleProgramChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">-- Select a program --</option>
                    {programs.map((program) => (
                      <option key={program.id} value={program.id}>{program.program_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Report Month *</label>
                  <input
                    type="month"
                    required
                    value={formData.report_month}
                    onChange={(e) => setFormData({ ...formData, report_month: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              {formData.producer_name && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">
                    <strong>Producer:</strong> {formData.producer_name} ({formData.producer_email})
                  </p>
                </div>
              )}
            </div>

            {/* Listener Feedback */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">💬 Listener Feedback</h3>
              <textarea
                value={formData.listener_feedback}
                onChange={(e) => setFormData({ ...formData, listener_feedback: e.target.value })}
                placeholder="Comments, calls, messages received about the program..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Impact Stories */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">🌟 Impact Stories</h3>
              <textarea
                value={formData.impact_stories}
                onChange={(e) => setFormData({ ...formData, impact_stories: e.target.value })}
                placeholder="Testimonies and life-changing stories from listeners..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">📡 Broadcast Metrics</h3>
                <textarea
                  value={formData.broadcast_metrics}
                  onChange={(e) => setFormData({ ...formData, broadcast_metrics: e.target.value })}
                  placeholder="Airtime logs, broadcast verification, number of broadcasts..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">📱 Digital Metrics</h3>
                <textarea
                  value={formData.digital_metrics}
                  onChange={(e) => setFormData({ ...formData, digital_metrics: e.target.value })}
                  placeholder="Social media engagement, online reach, website traffic..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Challenges & Recommendations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">⚠️ Challenges</h3>
                <textarea
                  value={formData.challenges}
                  onChange={(e) => setFormData({ ...formData, challenges: e.target.value })}
                  placeholder="Any issues encountered during the period..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">💡 Recommendations</h3>
                <textarea
                  value={formData.recommendations}
                  onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                  placeholder="Suggestions for improvement..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Additional Notes */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">📝 Additional Notes</h3>
              <textarea
                value={formData.additional_notes}
                onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                placeholder="Any other information to include in the report..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.program_id}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? '⏳ Creating...' : '📝 Create Draft Report'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// View Report Modal Component
const ViewReportModal = ({ report, onClose, onApprove, onSend }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">📊 {report.program_name}</h2>
              <p className="text-gray-500">Report for {report.report_month}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Producer:</span> <strong>{report.producer_name}</strong></div>
                <div><span className="text-gray-500">Email:</span> <strong>{report.producer_email}</strong></div>
                <div><span className="text-gray-500">Status:</span> <strong className="capitalize">{report.status}</strong></div>
                <div><span className="text-gray-500">Created:</span> <strong>{new Date(report.created_at).toLocaleDateString()}</strong></div>
                {report.approved_by && (
                  <>
                    <div><span className="text-gray-500">Approved By:</span> <strong>{report.approved_by}</strong></div>
                    <div><span className="text-gray-500">Approved:</span> <strong>{new Date(report.approved_at).toLocaleDateString()}</strong></div>
                  </>
                )}
              </div>
            </div>

            {report.listener_feedback && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">💬 Listener Feedback</h4>
                <p className="text-gray-700 whitespace-pre-line">{report.listener_feedback}</p>
              </div>
            )}

            {report.impact_stories && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">🌟 Impact Stories</h4>
                <p className="text-gray-700 whitespace-pre-line">{report.impact_stories}</p>
              </div>
            )}

            {report.broadcast_metrics && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-medium text-purple-800 mb-2">📡 Broadcast Metrics</h4>
                <p className="text-gray-700 whitespace-pre-line">{report.broadcast_metrics}</p>
              </div>
            )}

            {report.digital_metrics && (
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h4 className="font-medium text-indigo-800 mb-2">📱 Digital Metrics</h4>
                <p className="text-gray-700 whitespace-pre-line">{report.digital_metrics}</p>
              </div>
            )}

            {report.challenges && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">⚠️ Challenges</h4>
                <p className="text-gray-700 whitespace-pre-line">{report.challenges}</p>
              </div>
            )}

            {report.recommendations && (
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-medium text-orange-800 mb-2">💡 Recommendations</h4>
                <p className="text-gray-700 whitespace-pre-line">{report.recommendations}</p>
              </div>
            )}

            {report.additional_notes && (
              <div className="bg-gray-100 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">📝 Additional Notes</h4>
                <p className="text-gray-700 whitespace-pre-line">{report.additional_notes}</p>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-6 border-t mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
            {report.status === 'draft' && (
              <button
                onClick={onApprove}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                ✅ Approve Report
              </button>
            )}
            {report.status === 'approved' && (
              <button
                onClick={onSend}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                📤 Send to Producer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramReports;
